// A short, in-memory recording of explicitly supplied microphone frames. The caller owns the microphone and
// installs its one frame callback; this helper neither opens a microphone nor sends audio anywhere.
const MAX_RATE = 192_000;

/** Capture up to `seconds` of mono PCM; a stopped/abandoned capture keeps no frames. */
export function micClip({ seconds = 4, timeoutMs, onReady, onError }) {
  const duration = Number.isFinite(seconds) ? Math.max(1, Math.min(seconds, 10)) : 4;
  const timeout = Number.isFinite(timeoutMs) ? Math.max(1_000, Math.min(timeoutMs, 15_000)) : Math.max(10_000, duration * 1_000 + 5_000);
  let current = null, active = false, timer = 0, deadline = 0, wallDeadline = 0, rate = 0, count = 0, target = 0, samples = null;

  function clear(invalidate = true) {
    active = false; clearTimeout(timer); timer = 0; deadline = 0; wallDeadline = 0; rate = 0; count = 0; target = 0; samples = null;
    if (invalidate) current = null;
  }
  function fail(token) { if (!active || token !== current) return; clear(false); onError?.(token); }
  function finish(token) {
    if (!active || token !== current) return;
    const bytes = new Uint8Array(44 + count * 2), view = new DataView(bytes.buffer);
    const text = (offset, s) => { for (let i = 0; i < s.length; i++) bytes[offset + i] = s.charCodeAt(i); };
    text(0, 'RIFF'); view.setUint32(4, bytes.length - 8, true); text(8, 'WAVE'); text(12, 'fmt ');
    view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
    view.setUint32(24, rate, true); view.setUint32(28, rate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
    text(36, 'data'); view.setUint32(40, count * 2, true);
    let i = 44;
    for (const value of samples.subarray(0, count)) {
      const sample = Number.isFinite(value) ? Math.max(-1, Math.min(1, value)) : 0;
      view.setInt16(i, sample < 0 ? Math.round(sample * 32768) : Math.round(sample * 32767), true); i += 2;
    }
    const clip = new Blob([bytes], { type: 'audio/wav' }); clear(false); onReady?.(clip, token);
  }
  return {
    start() {
      clear(); const token = Symbol('mic-clip'); current = token; active = true;
      deadline = performance.now() + timeout; wallDeadline = Date.now() + timeout;
      timer = setTimeout(() => fail(token), timeout);
      return token;
    },
    /** The callback must capture its recording token; late frames from an older capture are ignored. */
    push(frame, token) {
      if (!active || token !== current) return;
      try {
        if (performance.now() >= deadline || Date.now() >= wallDeadline) return fail(token);
        const nextRate = frame.sampleRate, data = frame.getChannelData(0);
        if (!active || token !== current) return;
        if (!Number.isInteger(nextRate) || nextRate < 8_000 || nextRate > MAX_RATE || (rate && rate !== nextRate)) return fail(token);
        if (!rate) { rate = nextRate; target = Math.floor(rate * duration); samples = new Float32Array(target); }
        const take = Math.min(data.length, target - count);
        if (take > 0) { samples.set(data.subarray(0, take), count); count += take; }
        if (count === target) finish(token);
      } catch { fail(token); }
    },
    cancel: () => clear(),
    recording: () => active,
    isCurrent: (token) => token !== null && token === current,
  };
}
